import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { PrivatesAppointmentsApi } from "@/openapi/generated";
import type { BulkAppointmentBookingRequest } from "@/openapi/generated/models";
import type { BulkAppointmentBookingResponse } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { invalidateAndRefetchAppointmentBookings } from "../utils/appointment-bookings-query";
import { invalidatePostedTuitionLedgerQueries } from "@/features/tuition/hooks/invalidate-posted-tuition-ledger-queries";

type BookingMutationResult = BulkAppointmentBookingResponse & {
  needsVerification?: boolean;
};

function isDuplicateBookingError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes("already booked") ||
    lower.includes("duplicate") ||
    lower.includes("already exists") ||
    lower.includes("conflict")
  );
}

function getBookingFailureMessage(
  response: BulkAppointmentBookingResponse | undefined,
): string {
  const failed = response?.failed ?? [];
  const firstError = failed[0]?.error as { message?: string } | string | null | undefined;

  let message = "";
  if (typeof firstError === "string" && firstError.trim()) {
    message = firstError;
  } else if (firstError && typeof firstError === "object" && firstError.message) {
    message = firstError.message;
  }

  if (message && isDuplicateBookingError(message)) {
    return "This time slot is already booked for this student. Please choose a different time.";
  }

  if (message) {
    return message;
  }

  return "Failed to book private lessons. Please try again.";
}

function assertBookingsWereCreated(
  response: BookingMutationResult,
  totalRequested: number,
) {
  const createdCount = response.created?.length ?? 0;
  const failedCount = response.failed?.length ?? 0;

  if (createdCount > 0) {
    return;
  }

  if (failedCount > 0 || (!response.needsVerification && totalRequested > 0)) {
    throw new Error(getBookingFailureMessage(response));
  }
}

export const useAppointmentBook = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (
      bookingRequest: BulkAppointmentBookingRequest,
    ): Promise<BookingMutationResult> => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(
        PrivatesAppointmentsApi,
        accessToken,
      );

      const apiResponse = await service.apiAppointmentsBookPostRaw({
        bulkAppointmentBookingRequest: bookingRequest,
      });

      const responseText = await apiResponse.raw.clone().text();
      const totalRequested = bookingRequest.appointments?.length ?? 0;

      if (!responseText || responseText.trim() === "") {
        if (apiResponse.raw.status >= 200 && apiResponse.raw.status < 300) {
          return {
            created: [],
            failed: [],
            totalRequested,
            needsVerification: true,
          };
        }
        throw new Error("Failed to book private lessons. Please try again.");
      }

      let response: BookingMutationResult;
      try {
        response = (await apiResponse.value()) as BookingMutationResult;
      } catch (parseError) {
        if (apiResponse.raw.status >= 200 && apiResponse.raw.status < 300) {
          return {
            created: [],
            failed: [],
            totalRequested,
            needsVerification: true,
          };
        }
        throw parseError;
      }

      assertBookingsWereCreated(response, totalRequested);
      return response;
    },
    onSuccess: async (response, variables) => {
      const appointmentIds = [
        ...new Set(
          (variables.appointments ?? [])
            .map((appointment) => Number(appointment.id))
            .filter((id) => Number.isFinite(id) && id > 0),
        ),
      ];

      await invalidateAndRefetchAppointmentBookings(queryClient, appointmentIds);
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Appointments],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.AppointmentAvailability],
      });
      await invalidatePostedTuitionLedgerQueries(queryClient);

      const successCount = response?.created?.length ?? 0;
      const failedCount = response?.failed?.length ?? 0;
      const totalRequested =
        response?.totalRequested ?? variables?.appointments?.length ?? 0;

      if (successCount > 0 && failedCount === 0) {
        toast({
          title: "Booking successful",
          description: `Successfully booked ${successCount} private lesson(s)!`,
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        return;
      }

      if (successCount > 0 && failedCount > 0) {
        toast({
          title: "Partial success",
          description: `Booked ${successCount} private lesson(s), but ${failedCount} failed.`,
          status: "warning",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
        return;
      }

      if (response.needsVerification) {
        toast({
          title: "Booking successful",
          description: `Successfully booked ${totalRequested} private lesson(s)!`,
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      }
    },
    onError: (error: unknown) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to book private lesson";

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};
